import { Accordion } from "react-bootstrap";
import { useEffect, useState } from "react";
import { Problem } from "clinical-forms-experience-client";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { communicationApi } from "../../api-helpers/CommunicationApiHelper";
import JobAidModal from "../Appointment/JobAidModal";

interface Props {
  problems: Problem[];
  memberFormId: string;
  onChange: (selectedPGIs: Problem[]) => void;
  disabledPgiIds?: {
    problemId: string | undefined;
    goals: {
        goalId: string | undefined;
        interventions: (string | undefined)[];
    }[];
  }[];
}

const AssessmentPGIs = (props: Props) => {
  const [problems, setProblems] = useState<Problem[]>([]);
  const [selectedPGIs, setSelectedPGIs] = useState<Set<string>>(new Set());
  const [showJobAidModal, setShowJobAidModal] = useState(false);
  const [jobAidUrl, setJobAidUrl] = useState<string>('');
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isProblems, setIsProblems] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
});


useEffect(() => {
  if (!isProblems && props.problems.length > 0) {
    setProblems(props.problems);
    setIsProblems(true); 
  }
}, [props.problems, isProblems]); 

  const commApi = communicationApi();

  const handleShowJobAidModal = async (imageLocation: string) => {
    if(imageLocation === "") {
      setShowJobAidModal(false);
      return;
    }
    const communicationResponse = await commApi.communicationControllerGetSignedUrl(imageLocation);
    setJobAidUrl(communicationResponse.data);
    
    setShowJobAidModal(true);
  };

  useEffect(() => {
    // props.onChange(problems);
    const defaultOpenKeys = problems.flatMap((_, index) => [
      `problem-${index}`, 
      ..._.goals?.map((__, goalIndex) => `problem-${index}__goal-${goalIndex}`) || []
    ]);
    setOpenKeys(defaultOpenKeys);
  
    const newSelectedPGIs = new Set<string>();
    
    problems.forEach((problem, problemIndex) => {
      // Check if problem is disabled
      const disabledProblem = props.disabledPgiIds?.find(item => item.problemId === problem.id);
      
      if (disabledProblem) {
        newSelectedPGIs.add(`problem-${problemIndex}`);
      }
  
      problem.goals?.forEach((goal, goalIndex) => {
        // Check if goal is disabled
        const disabledGoal = disabledProblem?.goals.find(g => g.goalId === goal.id);
        
        if (goal.isDefaultGoal || disabledGoal) {
          newSelectedPGIs.add(`problem-${problemIndex}__goal-${goalIndex}`);
          newSelectedPGIs.add(`problem-${problemIndex}`);
        }
        
        goal.interventions?.forEach((intervention, interventionIndex) => {
          // Check if intervention is disabled
          const isDisabledIntervention = disabledGoal?.interventions.includes(intervention.id);
          
          if (intervention.isDefaultIntervention || isDisabledIntervention) {
            newSelectedPGIs.add(`problem-${problemIndex}__goal-${goalIndex}__intervention-${interventionIndex}`);
            newSelectedPGIs.add(`problem-${problemIndex}__goal-${goalIndex}`); 
            newSelectedPGIs.add(`problem-${problemIndex}`); 
          }
        });
      });
    });
    setSelectedPGIs(newSelectedPGIs);
  }, [problems, props.disabledPgiIds]);

  useEffect(() => {
    const selectedData = problems
      .map((problem, problemIndex) => {
        // Skip if problem is not selected
        if (!selectedPGIs.has(`problem-${problemIndex}`)) return null;
  
        const filteredGoals = problem.goals
          ?.map((goal, goalIndex) => {
            // Skip if goal is not selected
            const goalKey = `problem-${problemIndex}__goal-${goalIndex}`;
            if (!selectedPGIs.has(goalKey)) return null;
  
            const filteredInterventions = goal.interventions?.filter((_, interventionIndex) => {
              const interventionKey = `${goalKey}__intervention-${interventionIndex}`;
              return selectedPGIs.has(interventionKey);
            });
  
            return filteredInterventions && filteredInterventions.length > 0
              ? { ...goal, interventions: filteredInterventions }
              : null;
          })
          .filter(Boolean); // Remove null goals
  
        return filteredGoals && filteredGoals.length > 0
          ? { ...problem, goals: filteredGoals }
          : null;
      })
      .filter(Boolean) as Problem[]; // Remove null problems
    props.onChange(selectedData);
  }, [selectedPGIs]);
  

  const isDisabled = (problemIndex: number, goalIndex?: number, interventionIndex?: number) => {
    // Check if the problem is in the disabled list
    const problemId = problems[problemIndex].id;
    const disabledProblem = props.disabledPgiIds?.find(item => item.problemId === problemId);

    // If the problem is not disabled, return false
    if (!disabledProblem) {
        return false; 
    }

    // Check if the goal is disabled
    if (goalIndex !== undefined) {
        const goalId = problems[problemIndex].goals[goalIndex].id;
        const disabledGoal = disabledProblem.goals.find(g => g.goalId === goalId);

        // If the goal is not disabled, return false
        if (!disabledGoal) {
            return false; 
        }

        // Check if the intervention is disabled
        if (interventionIndex !== undefined) {
            const interventionId = problems[problemIndex].goals[goalIndex].interventions[interventionIndex].id;
            return disabledGoal.interventions.includes(interventionId);
        }

        // If the goal is disabled, return true
        return true; 
    }

    // If the problem is disabled, return true
    return true; 
};

  

  const handleAccordionToggle = (key: string) => {
    setOpenKeys(prevState => {
      // If the key is already open, collapse it, otherwise add it
      if (prevState.includes(key)) {
        return prevState.filter(k => k !== key);
      } else {
        return [...prevState, key];
      }
    });
  };

  const handleCheckboxChange = (key: string, type: "problem" | "goal" | "intervention") => {
    setSelectedPGIs((prevState) => {
      const updatedSet = new Set(prevState);
  
      if (updatedSet.has(key)) {
        if (type === "intervention") {
          const [problemKey, goalKey] = key.split("__");
          const goalFullKey = `${problemKey}__${goalKey}`;
          const problemIndex = parseInt(problemKey.split("-")[1]);
          const goalIndex = parseInt(goalKey.split("-")[1]);
          
          const goal = problems[problemIndex].goals?.[goalIndex];
          const isDefaultGoal = goal?.isDefaultGoal;
          
          // Check if this is the last selected intervention
          const selectedInterventionsCount = goal?.interventions?.filter((_, interventionIndex) => {
            const interventionKey = `${goalFullKey}__intervention-${interventionIndex}`;
            return updatedSet.has(interventionKey);
          }).length;
  
          if (isDefaultGoal && selectedInterventionsCount === 1) {
            setShowToast(true);
            setToastConfig({
              type: "warning",
              title: "Cannot Remove Intervention",
              message: "This goal has no other selected intervention.",
              icon: "warning"
            });
            return updatedSet;
          }
  
          updatedSet.delete(key);
          
          // Remove parent goal if it's not default and this was the last intervention
          if (!isDefaultGoal && selectedInterventionsCount === 1) {
            updatedSet.delete(goalFullKey);
          }
          return updatedSet;
        }
  
        updatedSet.delete(key);
  
        if (type === "problem") {
          removeDependentKeys(updatedSet, key, "goal", "intervention");
        } else if (type === "goal") {
          const [problemKey, goalKey] = key.split("__");
          const problemIndex = parseInt(problemKey.split("-")[1]);
          const goalIndex = parseInt(goalKey.split("-")[1]);
          
          problems[problemIndex].goals?.[goalIndex].interventions?.forEach((_, interventionIndex) => {
            updatedSet.delete(`${key}__intervention-${interventionIndex}`);
          });
        }
      } else {
        updatedSet.add(key);
  
        if (type === "intervention") {
          addParentKeys(updatedSet, key, "goal", "problem");
        } else if (type === "goal") {
          addParentKeys(updatedSet, key, "problem");
          const [problemKey, goalKey] = key.split("__");
          const problemIndex = parseInt(problemKey.split("-")[1]);
          const goalIndex = parseInt(goalKey.split("-")[1]);
          
          problems[problemIndex].goals?.[goalIndex].interventions?.forEach((_, interventionIndex) => {
            updatedSet.add(`${key}__intervention-${interventionIndex}`);
          });
        }
      }
  
      return updatedSet;
    });
  };
  

  const isProblemSelected = (problemIndex: number) =>
    selectedPGIs.has(`problem-${problemIndex}`);

  const isGoalSelected = (problemIndex: number, goalIndex: number) =>
    selectedPGIs.has(`problem-${problemIndex}__goal-${goalIndex}`);

  const isInterventionSelected = (
    problemIndex: number,
    goalIndex: number,
    interventionIndex: number
  ) =>
    selectedPGIs.has(
      `problem-${problemIndex}__goal-${goalIndex}__intervention-${interventionIndex}`
    );

  const addParentKeys = (set: Set<string>, key: string, ...parentTypes: string[]) => {
    const segments = key.split("__");
    for (let i = 0; i < parentTypes.length; i++) {
      const parentKey = segments.slice(0, segments.length - (i + 1)).join("__");
      if (parentKey) set.add(parentKey);
    }
  };

  const removeDependentKeys = (
    set: Set<string>,
    key: string,
    ...dependentTypes: string[]
  ) => {
    const prefix = key + "__";
    set.forEach((item) => {
      if (item.startsWith(prefix)) set.delete(item);
    });
  };

  const getFormattedDate = (defaultDueDays: number | null | undefined) => {
    const date = defaultDueDays
      ? new Date(Date.now() + defaultDueDays * 24 * 60 * 60 * 1000)
      : (() => {
        const now = new Date();
        // Create a new Date object for the same day next month
        // This ensures we do not modify the original 'now' date
        const nextMonth = new Date(now.setMonth(now.getMonth() + 1));
        return nextMonth;
      })();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <div className="pgi-table border rounded fs-12">
      <JobAidModal 
        jobAidUrl={jobAidUrl}
        showModal = {showJobAidModal}
        handleModal = {() => setShowJobAidModal(!showJobAidModal)}
      />
        <div className="row">
          <Toaster
              show={showToast}
              onClose={() => setShowToast(false)}
              type={toastConfig.type}
              title={toastConfig.title}
              message={toastConfig.message}
              icon={toastConfig.icon}
          />
        </div>
      <div className="d-flex bg-gray-FAF fw-bold">
        <div className="w-flex-lg flex-fill py-2 px-3 border-start">
          <strong>IDENTIFIED PROBLEM - GOAL - INTERVENTION</strong>
        </div>
        <div className="w-250px py-2 border-start text-center px-5">Due Date</div>
        <div className="w-50px py-2 border-start text-center">Select</div>
      </div>

        {problems.map((problem, problemIndex) => (
          <Accordion activeKey={openKeys} key={problemIndex}>
          <Accordion.Item eventKey={`problem-${problemIndex}`}>
            <div className="d-flex align-items-stretch border-top fs-12 problem-heading">
              <div className="w-flex-lg flex-fill py-2 px-4 border-start">
                  <Accordion.Header
                    onClick={() => handleAccordionToggle(`problem-${problemIndex}`)}
                  >
                    <a
                      data-bs-toggle="collapse"
                      className="accordion-link"
                    >
                      <strong className="d-block text-dark fw-bold">
                        Problem {problemIndex + 1}: {problem.title}
                      </strong>
                    </a>
                  </Accordion.Header>
              </div>
              <div className="w-50px border-start d-flex align-items-center justify-content-center">
                {/* <input
                  type="checkbox"
                  checked={isProblemSelected(problemIndex)}
                  onChange={() =>
                    handleCheckboxChange(`problem-${problemIndex}`, "problem")
                  }
                  disabled={isDisabled(problemIndex)} // Disable if the problem is in the disabled list
                /> */}
              </div>
            </div>
            <Accordion.Body className="p-0">
              {problem.goals?.map((goal, goalIndex) => (
                <Accordion.Item
                key={goalIndex} eventKey={`problem-${problemIndex}__goal-${goalIndex}`}
                >
                  <div className="d-flex align-items-stretch border-top fs-12">
                    <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 ps-12 border-start">
                      <div className="flex-fill ps-1">
                      <Accordion.Header
                        onClick={() =>
                          handleAccordionToggle(
                            `problem-${problemIndex}__goal-${goalIndex}`
                          )
                        }
                      >
                        <a
                          className="accordion-link"
                        >
                          <strong className="d-inline-block me-2">
                            <span className="text-light-black fw-600">
                              Goal {goalIndex + 1}: {goal.title}
                            </span>
                          </strong>
                        </a>
                      </Accordion.Header>
                      </div>
                    </div>
                    <div className="w-150px border-start text-center align-items-center d-flex px-5">
                      {getFormattedDate(goal.defaultDueDays)}
                    </div>
                    <div className="w-50px border-start d-flex align-items-center justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isGoalSelected(problemIndex, goalIndex)}
                        onChange={() =>
                          handleCheckboxChange(
                            `problem-${problemIndex}__goal-${goalIndex}`,
                            "goal"
                          )
                        }
                        disabled={isDisabled(problemIndex, goalIndex)} // Disable if the goal is in the disabled list
                      />
                    </div>
                  </div>
                  <Accordion.Body className="p-0">
                    {goal.interventions?.map((intervention, interventionIndex) => (
                      <div
                        key={interventionIndex}
                        className="d-flex align-items-stretch border-top fs-12"
                      >
                        <div className="w-flex-lg d-flex flex-fill align-items-center py-2 px-3 border-start ps-12">
                          <div className="flex-fill ps-6">
                            <div>
                              <span 
                                className={`material-symbols-outlined me-1 align-text-top fs-20 text-secondary cursor-pointer ${intervention?.imageLocation ? 'visible' : 'invisible'}`}
                                onClick={() => handleShowJobAidModal(intervention?.imageLocation || '')}
                              >
                                quick_reference
                              </span>
                              <span className="intervention-badge badge badge-offwhite me-1 border">
                                Intervention
                              </span>
                              <span className="badge badge-light me-1">
                                {/* Low */}
                              </span>
                              <span className="me-2 text-light-black">{intervention.title}</span>
                            </div>
                          </div>
                        </div>
                        <div className="w-150px border-start text-center align-items-center d-flex px-5">
                          {getFormattedDate(intervention.defaultDueDays)}
                        </div>
                        <div className="w-50px border-start d-flex align-items-center justify-content-center">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isInterventionSelected(
                              problemIndex,
                              goalIndex,
                              interventionIndex
                            )}
                            onChange={() =>
                              handleCheckboxChange(
                                `problem-${problemIndex}__goal-${goalIndex}__intervention-${interventionIndex}`,
                                "intervention"
                              )
                            }
                            disabled={isDisabled(problemIndex, goalIndex, interventionIndex)} // Disable if the intervention is in the disabled list
                          />
                        </div>
                      </div>
                    ))}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion.Body>
          </Accordion.Item>
          </Accordion>
        ))}
      
    </div>
  );
};

export default AssessmentPGIs;
